import { useId } from 'react';

import { ComboField, TextField } from '~/components/form';
import {
  rangeForPreset,
  toIsoBoundary,
  toLocalDateValue,
  type AnalyticsDateRangePreset,
  type AnalyticsDateRangeValue,
} from '~/lib/ai/analytics';

const PRESET_LABELS: Record<AnalyticsDateRangePreset, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  custom: 'Custom',
  all: 'All time',
};

/** Every preset this control knows, in display order. */
export const ALL_DATE_PRESETS = Object.keys(PRESET_LABELS) as AnalyticsDateRangePreset[];

export interface DateRangeProps {
  value: AnalyticsDateRangeValue;
  onChange: (value: AnalyticsDateRangeValue) => void;
  /** Combined with the control names for test ids: `<testId>-preset`, `-start`, `-end`. */
  testId: string;
  id?: string;
  /** The presets to offer; a range whose endpoint cannot honour one hides it rather than offering an inert choice. */
  presets?: AnalyticsDateRangePreset[];
}

/** Analytics date range: a preset, plus explicit start/end dates while "Custom" is chosen. */
export default function DateRange({
  value,
  onChange,
  testId,
  id,
  presets = ALL_DATE_PRESETS,
}: DateRangeProps) {
  const generatedId = useId();
  const prefix = id ?? generatedId;

  const selectPreset = (preset: AnalyticsDateRangePreset) => {
    if (preset === 'custom' || preset === 'all') {
      onChange({ preset, range: value.range });
      return;
    }

    onChange({ preset, range: rangeForPreset(preset) });
  };

  const setCustomDate = (field: 'endDate' | 'startDate', raw: string) => {
    if (!raw) {
      return;
    }

    onChange({ preset: 'custom', range: { ...value.range, [field]: toIsoBoundary(raw, field) } });
  };

  return (
    <div className="space-y-3">
      <ComboField
        id={`${prefix}-preset`}
        ariaLabel="Date range"
        testId={`${testId}-preset`}
        value={value.preset}
        options={presets.map((preset) => ({ value: preset, label: PRESET_LABELS[preset] }))}
        onChange={(next) => selectPreset(next as AnalyticsDateRangePreset)}
        searchable
        compact
      />

      {value.preset === 'custom' ? (
        <div className="grid grid-cols-2 gap-4">
          <TextField
            id={`${prefix}-start`}
            label="Start date"
            type="date"
            testId={`${testId}-start`}
            value={toLocalDateValue(value.range.startDate)}
            onChange={(raw) => setCustomDate('startDate', raw)}
          />
          <TextField
            id={`${prefix}-end`}
            label="End date"
            type="date"
            testId={`${testId}-end`}
            value={toLocalDateValue(value.range.endDate)}
            onChange={(raw) => setCustomDate('endDate', raw)}
          />
        </div>
      ) : null}
    </div>
  );
}
